import { useState } from "react";
import {
  Archive,
  Check,
  MoreHorizontal,
  Trash2,
  UserMinus,
} from "lucide-react";
import { toast } from "sonner";
import { type Trip, TripStatus } from "@/api.types.v2";
import {
  useArchiveTrip,
  useDeleteTrip,
  useSetTripColor,
  useUnassignOperatorFromTrip,
} from "@/api/trips";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TRIP_COLORS } from "./trip-colors";

/**
 * Kebab actions menu for a dispatch trip card: set trip color, unassign the
 * driver, archive the trip (COMPLETED only), delete the trip. Delete is
 * confirmed; color/unassign/archive apply immediately.
 */
export function TripActionsMenu({ trip }: { trip: Trip }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const setColor = useSetTripColor(trip.id, {
    onError: () => toast.error("Couldn't update trip color"),
  });
  const unassign = useUnassignOperatorFromTrip(trip.id, {
    onSuccess: () => toast.success("Driver unassigned"),
    onError: () => toast.error("Couldn't unassign driver"),
  });
  const archive = useArchiveTrip(trip.id, {
    onSuccess: () => toast.success("Trip archived"),
    onError: () => toast.error("Couldn't archive trip"),
  });
  const deleteTrip = useDeleteTrip();

  // Server only allows archiving a COMPLETED trip.
  const canArchive = trip.status === TripStatus.COMPLETED;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="Trip actions"
            className="grid h-7 w-7 place-items-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          >
            <MoreHorizontal className="h-[15px] w-[15px]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
            Trip color
          </DropdownMenuLabel>
          <div className="flex flex-wrap gap-1.5 px-2 pb-1.5 pt-0.5">
            {TRIP_COLORS.map((c) => {
              const selected = trip.color === c;
              return (
                <button
                  key={c}
                  type="button"
                  title={c}
                  disabled={setColor.isPending}
                  onClick={() => setColor.mutate(c)}
                  className="grid h-6 w-6 place-items-center rounded-full transition-transform hover:scale-110 disabled:opacity-60"
                  style={{
                    background: c,
                    boxShadow: selected
                      ? `0 0 0 2px white, 0 0 0 4px ${c}`
                      : undefined,
                  }}
                >
                  {selected && <Check className="h-3.5 w-3.5 text-white" />}
                </button>
              );
            })}
          </div>
          <DropdownMenuSeparator />
          {trip.operatorId && (
            <DropdownMenuItem
              disabled={unassign.isPending}
              onClick={() => unassign.mutate()}
            >
              <UserMinus className="mr-2 h-4 w-4" />
              Unassign driver
            </DropdownMenuItem>
          )}
          {canArchive && (
            <DropdownMenuItem
              disabled={archive.isPending}
              onClick={() => archive.mutate()}
            >
              <Archive className="mr-2 h-4 w-4" />
              Archive trip
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="text-red-600 focus:bg-red-50 focus:text-red-700"
            onSelect={(e) => {
              e.preventDefault();
              setConfirmDelete(true);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete trip
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete trip #{trip.tripNumber}?</AlertDialogTitle>
            <AlertDialogDescription>
              The trip's stops return to the unassigned pool and can be
              re-routed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() =>
                deleteTrip.mutate(trip.id, {
                  onSuccess: () => toast.success("Trip deleted"),
                  onError: () => toast.error("Couldn't delete trip"),
                })
              }
            >
              Delete trip
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
